import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAdminAuth } from "@/contexts/AdminAuth";
import { adminHttp } from "@/lib/api";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import PropertiesPanel from "@/components/admin/PropertiesPanel";
import BlogsPanel from "@/components/admin/BlogsPanel";
import LeadsPanel from "@/components/admin/LeadsPanel";
import SettingsPanel from "@/components/admin/SettingsPanel";
import SiteContentPanel from "@/components/admin/SiteContentPanel";
import { Home, FileText, Users, Settings, LogOut, Loader2, Type } from "lucide-react";

const TABS = [
  { id: "properties", label: "Properties", icon: Home },
  { id: "blogs", label: "Journal", icon: FileText },
  { id: "site-content", label: "Site Content", icon: Type },
  { id: "leads", label: "Leads", icon: Users },
  { id: "settings", label: "Settings", icon: Settings },
];

export default function Admin() {
  const { user, loading, logout } = useAdminAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("properties");
  const [counts, setCounts] = useState({ properties: 0, blogs: 0, leads: 0 });

  useEffect(() => {
    if (!user) return;
    Promise.all([
      adminHttp.get("/properties"),
      adminHttp.get("/blogs"),
      adminHttp.get("/admin/leads"),
    ])
      .then(([p, b, l]) =>
        setCounts({ properties: p.data.length, blogs: b.data.length, leads: l.data.length })
      )
      .catch(() => {});
  }, [user, tab]);

  if (loading) {
    return (
      <main className="min-h-screen bg-[#f5f0e8] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#c5a059]" size={24} />
      </main>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  const doLogout = () => {
    logout();
    toast.success("Signed out.");
    nav("/admin/login");
  };

  return (
    <main data-testid="admin-page" className="min-h-screen bg-[#f5f0e8] text-[#1c1c1c] grain">
      {/* Top bar */}
      <header className="border-b border-[#1c1c1c]/10 bg-[#f5f0e8]/95 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-10">
            <a href="/" className="flex items-center gap-3">
              <Logo size="sm" />
            </a>
            <span className="hidden md:block underline-label text-[#6a6259]">Private Console</span>
          </div>
          <div className="flex items-center gap-5">
            <div className="hidden sm:block text-[11px] uppercase tracking-[0.28em] text-[#6a6259]">
              {user.email}
            </div>
            <button
              data-testid="admin-logout"
              onClick={doLogout}
              className="inline-flex items-center gap-2 px-4 py-2 border border-[#c5a059]/40 text-[#c5a059] text-[10px] uppercase tracking-[0.28em] hover:bg-[#c5a059] hover:text-black transition-colors"
            >
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Sidebar */}
        <aside className="lg:col-span-3">
          <nav className="space-y-1">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`admin-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left text-[11px] uppercase tracking-[0.28em] transition-all border ${
                    active
                      ? "border-[#c5a059] bg-[#c5a059] text-black"
                      : "border-transparent text-[#3d2b1f]/70 hover:border-[#c5a059]/40 hover:text-[#c5a059]"
                  }`}
                >
                  <span className="inline-flex items-center gap-3">
                    <Icon size={14} />
                    {t.label}
                  </span>
                  <span className={`text-[10px] tabular-nums ${active ? "text-black/70" : "text-[#6a6259]"}`}>
                    {counts[t.id === "blogs" ? "blogs" : t.id] ?? ""}
                  </span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main panel */}
        <section className="lg:col-span-9">
          {tab === "properties" && <PropertiesPanel />}
          {tab === "blogs" && <BlogsPanel />}
          {tab === "site-content" && <SiteContentPanel />}
          {tab === "leads" && <LeadsPanel />}
          {tab === "settings" && <SettingsPanel />}
        </section>
      </div>
    </main>
  );
}
